import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import Routes, { DELIVERY_ID_PARAM, SET_ID_PARAM } from 'routes';
import API from 'api/API';
import { TrayLabelInfo } from 'api/models/Deliveries';
import { useDelivery } from 'api/hooks/useDelivery';
import { useOpenSetLabelsPdf } from 'api/hooks/useOpenSetLabelsPdf';
import { useUpdateDeliveryInspection } from 'api/hooks/useUpdateDeliveryInspection';
import { CheckInSet, useCheckInTraysBySet } from 'hooks/useCheckInTraysBySet';
import { useSaveCheckInTrayInspectionsForSet } from 'hooks/useSaveCheckInTrayInspectionsForSet';
import { getSetDisplayLabel } from 'hooks/useSetDisplayLabel';
import { useSaviAuthToken } from 'hooks/useSaviAuthToken';
import { VisibleDialog } from 'components/CheckInTrayDialogs';

type SaveAndCompleteCheckInOptions = {
  checkInSet: CheckInSet;
  setKitInstanceId: string;
  checkInTrayId: string | string[];
  setVisibleDialog?: (dialog: VisibleDialog | null) => void;
  isDecant?: boolean;
  skipInspection?: boolean;
};

export const useSaveAndCompleteCheckIn = () => {
  const navigate = useNavigate();
  const { deliveryId = '' } = useParams();
  const { data: delivery, refetch: fetchDelivery } = useDelivery(deliveryId);
  const { openSetLabelsPdf } = useOpenSetLabelsPdf();
  const { getFreshSaviAuthToken } = useSaviAuthToken();
  const { mutateAsync: saveCheckInTrayInspectionsForSet, isLoading: saveCheckInTrayInspectionsForSetLoading } =
    useSaveCheckInTrayInspectionsForSet();
  const { mutateAsync: updateDeliveryInspection } = useUpdateDeliveryInspection();
  const [, setCheckInTraysBySet] = useCheckInTraysBySet();

  // warm the savi auth token cache as check-in starts so any refresh network call doesn't add
  // latency at the point of use; useOpenSetLabelsPdf re-checks freshness right before printing
  useEffect(() => {
    getFreshSaviAuthToken().catch(console.error);
  }, [getFreshSaviAuthToken]);

  const saveAndComplete = useCallback(
    async ({
      checkInSet,
      setKitInstanceId,
      checkInTrayId,
      setVisibleDialog,
      isDecant = false,
      skipInspection = false,
    }: SaveAndCompleteCheckInOptions) => {
      if (!delivery) {
        throw new Error('No delivery found');
      }

      try {
        const allTraysCheckedIn = Object.values(checkInSet.trays).every(tray => tray.completed);

        if (allTraysCheckedIn) {
          await saveCheckInTrayInspectionsForSet({ setKitInstanceId, checkInSet, skipInspection });
        }

        // the just-checked-in tray isn't persisted until the whole set completes, so the label
        // data can't be looked up in the BE yet - build it from local state and pass it through.
        // A decanted tray prints one label per decant; a plain tray prints a single label.
        const deliverySet = delivery.sets?.find(set => set.kitInstanceId === setKitInstanceId);
        const setName = getSetDisplayLabel(deliverySet) || checkInSet.name || '';

        const checkInTrayIds = Array.isArray(checkInTrayId) ? checkInTrayId : [checkInTrayId];
        const labels: TrayLabelInfo[] = checkInTrayIds.flatMap(id => {
          const localTray = checkInSet.trays[id];
          if (!localTray) return [];

          const labelSources = isDecant ? localTray.decantedTrays || [] : [localTray];
          return labelSources.map(source => ({
            setName,
            trayNumber: source.trayReference ?? 0,
            assetNumber: source.assetNumber || undefined,
          }));
        });

        const allImplantsConfirmed =
          delivery?.implants?.every(implant => implant.inspection?.CHECK_IN?.status === 'CONFIRMED') ?? true;
        const allSetInspectionsConfirmed =
          delivery?.sets?.every(set => set.inspections?.CHECK_IN?.status === 'CONFIRMED') ?? true;

        if (checkInSet.postCheckinTrayAdd && allTraysCheckedIn && allImplantsConfirmed) {
          // not awaited - opens as soon as the tab is available
          openSetLabelsPdf(deliveryId, labels).catch(console.error);

          await updateDeliveryInspection({
            deliveryId,
            inspection: {
              stage: 'CHECK_IN',
              status: 'COMPLETED',
            },
          });

          setCheckInTraysBySet({});
          navigate(
            Routes.SetDetails.path.replace(DELIVERY_ID_PARAM, deliveryId).replace(SET_ID_PARAM, setKitInstanceId),
          );
          return;
        }

        // not awaited - opens as soon as the tab is available
        openSetLabelsPdf(deliveryId, labels).catch(console.error);

        const { data: updatedDelivery } = await fetchDelivery();

        if (!updatedDelivery) {
          throw new Error('No updated delivery found');
        }

        if (updatedDelivery?.status === 'CHECKED_IN') {
          setVisibleDialog?.('DeliveryCheckedIn');
        } else {
          if (!allImplantsConfirmed && allTraysCheckedIn && allSetInspectionsConfirmed) {
            setVisibleDialog?.('SetsCheckedIn');
          } else {
            navigate(Routes.CheckInWorkflow.path.replace(DELIVERY_ID_PARAM, deliveryId));
          }
        }
      } catch (error) {
        API.errorHandler(error);
      }
    },
    [
      delivery,
      deliveryId,
      fetchDelivery,
      navigate,
      openSetLabelsPdf,
      saveCheckInTrayInspectionsForSet,
      setCheckInTraysBySet,
      updateDeliveryInspection,
    ],
  );

  return {
    saveAndComplete,
    isLoading: saveCheckInTrayInspectionsForSetLoading,
  };
};
